import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import Theme from '@styles/Theme';
import {
    CollectionPath,
    SettingCollectionPath,
} from '@typings/collections.enum';
import { Ijukebox, IJukeboxItem } from '@typings/setting/jukebox.interface';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import IconButton from '@components/dataEntry/button/IconButton';
import RangeSlider from '@components/dataEntry/rangeSlider';

const sizeMap = {
    md: css`
    padding: 16px;
    gap: 24px;
  `,
    sm: css`
    padding: 8px;
    gap: 8px;
    flex-direction: column;
    text-align: center;
  `,
};

const MiniPlayerContainer = styled.div`
  border-radius: 8px;
  overflow: hidden;
`;

const CurrentAlbumSec = styled.div<{
    size?: 'md' | 'sm';
}>`
  display: flex;
  align-items: center;

  ${({ size }) => size && sizeMap[size]}
`;

const ProgressSec = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr 28px;
  align-items: center;
  gap: 8px;
  font-size: 10px;
`;

const AlbumSec = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 50%;
  box-shadow: ${Theme.shadow.dropdown};
  background: center / cover no-repeat var(--backgroundColor);
  animation: spin-slow 20s linear infinite;

  @keyframes spin-slow {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const TitleSec = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;

  h6 {
    font-size: 16px;
  }
  .tag {
    font-size: 9px;
    margin-bottom: 4px;
  }
  .title {
    font-family: var(--headerFontFamily);
  }
  .subTitle {
    font-size: 10px;
  }
`;

const BtnWrap = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-around;
`;

type Props = {
    style?: React.CSSProperties;
    size?: 'md' | 'sm';
    currentIndex?: number;
    onIndexChange?: (index: number) => void;
};

function MiniPlayer({ style, size = 'md', currentIndex = 0, onIndexChange }: Props) {
    const { data: playlist } = useCollection<IJukeboxItem>(
        CollectionPath.JUKEBOX,
        { listen: true, orderBy: ['order', 'asc'] },
    );

    const { data: jukebox } = useDocument<Ijukebox>(
        SettingCollectionPath.JUKEBOX,
        { listen: true },
    );

    const [index, setIndex] = useState(currentIndex);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    const audioRef = useRef<HTMLAudioElement>(null);
    const current = playlist?.[index];

    useEffect(() => {
        setIndex(currentIndex);
    }, [currentIndex]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateProgress = () => {
            setProgress(audio.currentTime);
            setDuration(audio.duration || 0);
        };

        const handleAudioEnd = () => {
            const nextIndex = (index + 1) % playlist!.length;
            setIndex(nextIndex);
            onIndexChange?.(nextIndex);
        };

        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('loadedmetadata', updateProgress);
        audio.addEventListener('ended', handleAudioEnd);

        return () => {
            audio.removeEventListener('timeupdate', updateProgress);
            audio.removeEventListener('loadedmetadata', updateProgress);
            audio.removeEventListener('ended', handleAudioEnd);
        };
    }, [index, playlist, onIndexChange]);

    useEffect(() => {
        isPlaying ? audioRef.current?.play() : audioRef.current?.pause();
    }, [isPlaying, index]);

    const handlePlayPause = () => setIsPlaying(!isPlaying);

    const handleNext = () => {
        const nextIndex = (index + 1) % playlist!.length;
        setIndex(nextIndex);
        onIndexChange?.(nextIndex);
    };

    const handlePrev = () => {
        const prevIndex = (index - 1 + playlist!.length) % playlist!.length;
        setIndex(prevIndex);
        onIndexChange?.(prevIndex);
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const time = Number(e.target.value);
        audioRef.current!.currentTime = time;
        setProgress(time);
    };

    const formatTime = (sec: number) =>
        isNaN(sec)
            ? '--:--'
            : `${Math.floor(sec / 60)}:${('0' + Math.floor(sec % 60)).slice(-2)}`;

    return (
        <MiniPlayerContainer
            style={{
                backgroundColor: jukebox?.themeSet?.backgroundColor,
                color: jukebox?.themeSet?.textColor,
                ...style,
            }}
        >
            <CurrentAlbumSec size={size}>
                <AlbumSec
                    style={{ backgroundImage: `url('${current?.imgURL || ''}')` }}
                />
                {current?.file?.url && (
                    <audio
                        ref={audioRef}
                        src={current?.file?.url || ''}
                        preload="metadata"
                    />
                )}
                <TitleSec
                    style={{
                        color: jukebox?.themeSet?.subTextColor,
                    }}
                >
                    <b className="tag">Now Playing...</b>
                    <h6
                        className="title"
                        style={{
                            color: jukebox?.themeSet?.textColor,
                        }}
                    >
                        {current?.title}
                    </h6>
                    <div className="subTitle">{current?.file?.name}</div>
                    <ProgressSec>
                        <span>{formatTime(progress)}</span>
                        <RangeSlider
                            shape="sm"
                            id="now-play"
                            value={progress}
                            onChange={handleSeek}
                            min={0}
                            max={duration}
                            step={0.1}
                            showInput={false}
                            primaryColor={jukebox?.themeSet?.buttonColor}
                            bgColor={jukebox?.themeSet?.lineColor}
                        />
                        <span>{formatTime(duration)}</span>
                    </ProgressSec>
                    <BtnWrap>
                        <IconButton
                            size="sm"
                            icon="PlayPrev14"
                            onClick={handlePrev}
                            shape="default"
                            iconColor={jukebox?.themeSet?.textColor}
                        />
                        <IconButton
                            icon={isPlaying ? 'Pause20' : 'Play20'}
                            onClick={handlePlayPause}
                            shape="default"
                            iconColor={jukebox?.themeSet?.textColor}
                        />
                        <IconButton
                            size="sm"
                            icon="PlayNext14"
                            onClick={handleNext}
                            shape="default"
                            iconColor={jukebox?.themeSet?.textColor}
                        />
                    </BtnWrap>
                </TitleSec>
            </CurrentAlbumSec>
        </MiniPlayerContainer>
    );
}

export default MiniPlayer;